import { DockviewApi } from 'dockview';
import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useAddTab } from '@/features/tab/add-tab/model/useAddTab';
import { deserialize } from '@/entities/tab/model/path';
import { useAuth } from '@/entities/auth/model/useAuth';
import { workspaceConfigAtom } from '@/entities/workspace-config/model/workspace-config.atom';
import type { UserRole } from '@/entities/auth/model/auth.types';
import type { DefaultPagesConfig } from '@/entities/workspace-config';

export const FALLBACK_DEFAULT_PAGES: DefaultPagesConfig = {
  Planner: '/workspace/my/planner',
  Operator: '/workspace/home',
  Staff: '/workspace/home',
  Administrator: '/workspace/home',
  Engineer: '/workspace/home',
};

export function resolveDefaultPath(
  defaultPages: DefaultPagesConfig | undefined,
  role: UserRole | undefined
): string {
  const pages = defaultPages ?? FALLBACK_DEFAULT_PAGES;
  if (role && pages[role]) return pages[role]!;
  return FALLBACK_DEFAULT_PAGES[role ?? 'Operator'] ?? '/workspace/home';
}

/**
 * 모든 탭이 닫히면 해당 역할의 기본 페이지를 자동으로 엽니다.
 * 기본 페이지는 WorkspaceConfig.defaultPages에서 역할별로 설정됩니다.
 */
export function useEnsureEmptyTab(api: DockviewApi | null) {
  const { addTab } = useAddTab(api);
  const { user } = useAuth();
  const [config] = useAtom(workspaceConfigAtom);

  useEffect(() => {
    if (!api) return;

    const ensureTab = () => {
      if (api.panels.length === 0) {
        const defaultPath = resolveDefaultPath(config?.defaultPages, user?.role);
        addTab(deserialize(defaultPath));
      }
    };

    // api 마운트 시 즉시 확인: useTabStateLoader가 api.fromJSON으로 빈 레이아웃을 복원한 경우
    // onDidLayoutChange 이벤트가 리스너 등록 전에 발화되어 놓칠 수 있으므로 초기 체크 필요
    ensureTab();

    const disposable = api.onDidLayoutChange(ensureTab);

    return () => disposable.dispose();
  }, [api, addTab, config, user?.role]);
}
